import { useEffect, useState } from 'react';
import { bundleService } from '../../../services/bundleService';
import { useNavigate } from 'react-router-dom';

export default function Bundles() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('my-bundles');
  const [bundles, setBundles] = useState([]); // my bundles
  const [availableBundles, setAvailableBundles] = useState([]); // bundles to buy
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  async function loadMyBundles() {
    try {
      const data = await bundleService.mine(token);
      setBundles(data?.bundles || []);
    } catch (e) {
      setError(e?.message || 'Failed to load my bundles');
    }
  }

  async function loadAvailableBundles() {
    try {
      const data = await bundleService.available(token);
      setAvailableBundles(data?.bundles || []);
    } catch (e) {
      setError(e?.message || 'Failed to load available bundles');
    }
  }

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      if (activeTab === 'my-bundles') {
        await loadMyBundles();
      } else {
        await loadAvailableBundles();
      }
      setLoading(false);
    })();
  }, [activeTab]);

  async function handlePurchase(id) {
    if (!confirm('Are you sure you want to purchase this bundle?')) return;
    setPurchasing(id);
    setError('');
    setSuccess('');
    try {
      const res = await bundleService.purchase(id, token);
      if (res && res.authorization_url) {
        setSuccess('Redirecting to payment gateway...');
        window.location.href = res.authorization_url;
        return;
      }
      setSuccess('Purchase requested! Please complete payment or wait for approval.');
      // Refresh available bundles or switch to my bundles?
      // Let's stay here and maybe show success message.
      // Or maybe refresh to show it's "purchased" if we track that?
      // For now just success message.
    } catch (e) {
      setError(e?.message || 'Purchase failed');
    } finally {
      setPurchasing(null);
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold">Bundles</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('my-bundles')}
            className={`px-4 py-2 rounded transition-colors ${activeTab === 'my-bundles' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            My Bundles
          </button>
          <button
            onClick={() => setActiveTab('buy-bundles')}
            className={`px-4 py-2 rounded transition-colors ${activeTab === 'buy-bundles' ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          >
            Buy Bundles
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded border border-red-200">{error}</div>}
      {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded border border-green-200">{success}</div>}

      {loading ? (
        <div className="bg-white rounded shadow p-8 text-center text-gray-500">Loading...</div>
      ) : activeTab === 'my-bundles' ? (
        bundles.length === 0 ? (
          <div className="bg-white rounded shadow p-8 text-center">
            <p className="text-gray-700 mb-4">You have no active bundles.</p>
            <button
              onClick={() => setActiveTab('buy-bundles')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
            >
              Browse Available Bundles
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {bundles.map(b => {
              const remaining = b.remaining;
              const exp = b.expiryDate ? new Date(b.expiryDate).toLocaleDateString() : '—';
              const start = b.startDate ? new Date(b.startDate).toLocaleDateString() : '—';
              const statusColor =
                b.status === 'expired' ? 'text-gray-600 bg-gray-100' :
                b.status === 'active' ? 'text-green-700 bg-green-100' :
                'text-yellow-700 bg-yellow-100';
              return (
                <div key={b.id} className="bg-white rounded-lg shadow p-5 border border-gray-100">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-800">{b.bundleName}</h3>
                    <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusColor} capitalize`}>{b.status}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Remaining</p>
                      <p className="font-semibold text-gray-800">{remaining === null ? 'Unlimited' : remaining}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Expiry</p>
                      <p className="font-semibold text-gray-800">{exp}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Start Date</p>
                      <p className="font-semibold text-gray-800">{start}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-3 border-t border-gray-100 text-xs text-gray-500">
                    Auto-applies to shipments.
                  </div>
                </div>
              );
            })}
          </div>
        )
      ) : (
        /* Buy Bundles Tab */
        availableBundles.length === 0 ? (
          <div className="bg-white rounded shadow p-8 text-center text-gray-500">
            No bundles currently available for purchase.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {availableBundles.map(b => (
              <div key={b._id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
                <div className="p-5 border-b border-gray-100 bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{b.name}</h3>
                      <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 capitalize">
                        {b.category || 'Local'}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-gray-900">₦{Number(b.price).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
                <div className="p-5">
                  <ul className="space-y-3 text-sm text-gray-600 mb-6">
                    <li className="flex items-center">
                      <span className="w-5 text-center mr-2">📦</span>
                      {b.usageLimit ? `${b.usageLimit} Shipments` : 'Unlimited Shipments'}
                    </li>
                    <li className="flex items-center">
                      <span className="w-5 text-center mr-2">⏳</span>
                      {b.durationDays ? `${b.durationDays} Days Duration` : 'No Expiry'}
                    </li>
                    <li className="flex items-center">
                      <span className="w-5 text-center mr-2">🌍</span>
                      {b.category === 'interstate' ? 'Interstate Only' : 'Local Only'}
                    </li>
                  </ul>
                  <button
                    onClick={() => handlePurchase(b._id)}
                    disabled={purchasing === b._id}
                    className="w-full py-2.5 px-4 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 focus:ring-4 focus:ring-red-100 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {purchasing === b._id ? 'Processing...' : 'Purchase Bundle'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
